import { useEffect, useRef, useState } from 'react'
import { Autocomplete, AutocompleteItem, Avatar, Divider, Input } from '@heroui/react'
import type { Key } from 'react'
import { isCountryCode, parsePhone, toE164, type CountryCode } from '@leangap/types'
import {
	PHONE_COUNTRY_OPTIONS,
	phoneCountryDisplayName,
	sanitizeNationalNumber,
	type PhoneCountryOption,
} from '@/utils/phone'

type Props = {
	label?: string
	value?: string
	onValueChange: (value: string) => void
	onBlur?: () => void
	errorMessage?: string
	isInvalid?: boolean
	isRequired?: boolean
	isReadOnly?: boolean
	isDisabled?: boolean
	isClearable?: boolean
	onClear?: () => void
	className?: string
}

type PhoneCountry = CountryCode | ''

type PhoneCountryItem = PhoneCountryOption | { code: 'divider'; label: ''; callingCode: '' }

const PHONE_COUNTRY_ITEMS: PhoneCountryItem[] = [
	...PHONE_COUNTRY_OPTIONS.slice(0, 3),
	{ code: 'divider', label: '', callingCode: '' },
	...PHONE_COUNTRY_OPTIONS.slice(3),
]

function callingCodeFor(country: PhoneCountry): string {
	if (!country) return ''
	const option = PHONE_COUNTRY_OPTIONS.find((item) => item.code === country)
	return option ? `+${option.callingCode}` : ''
}

function countryFromValue(value: string | undefined): PhoneCountry {
	return parsePhone(value ?? '')?.country ?? ''
}

function nationalFromValue(value: string | undefined): string {
	if (!value) return ''
	return parsePhone(value)?.nationalNumber ?? sanitizeNationalNumber(value)
}

function isPhoneCountryOption(item: PhoneCountryItem): item is PhoneCountryOption {
	return item.code !== 'divider'
}

function matchesPhoneCountry(textValue: string, inputValue: string) {
	const query = inputValue.trim().toLowerCase()
	if (!query) return true
	const haystack = textValue.toLowerCase()
	if (haystack.includes(query)) return true
	const digits = query.replace(/^\+/, '')
	return Boolean(digits) && haystack.includes(`(+${digits})`)
}

export default function PhoneInput({
	label = 'Phone Number',
	value = '',
	onValueChange,
	onBlur,
	errorMessage,
	isInvalid,
	isRequired = false,
	isReadOnly = false,
	isDisabled = false,
	isClearable = false,
	onClear,
	className,
}: Props) {
	const [country, setCountry] = useState<PhoneCountry>(() => countryFromValue(value))
	const [national, setNational] = useState(() => nationalFromValue(value))
	const [pickerEpoch, setPickerEpoch] = useState(0)
	const countryPickerUsed = useRef(false)

	useEffect(() => {
		if (!value) {
			setNational('')
			return
		}
		if (!value.trim().startsWith('+')) {
			setNational(sanitizeNationalNumber(value))
			return
		}
		const parsed = parsePhone(value)
		if (parsed) {
			setCountry(parsed.country)
			setNational(parsed.nationalNumber)
		}
	}, [value])

	const emit = (nextCountry: PhoneCountry, nextNational: string) => {
		setCountry(nextCountry)
		setNational(nextNational)
		if (!nextCountry || !nextNational) {
			onValueChange(nextNational)
			return
		}
		onValueChange(toE164(nextNational, nextCountry))
	}

	const locked = isReadOnly || isDisabled
	const missingCountry = Boolean(!country && national)
	const countryInvalid = Boolean(isInvalid && missingCountry)
	const numberInvalid = Boolean(isInvalid)

	// Selection changes are only honoured after real user interaction with the picker,
	// so programmatic remounts never emit. Track pointer, keyboard, and focus so that
	// keyboard-only users (Tab -> type -> Enter) can select a country too.
	const markPickerUsed = () => {
		countryPickerUsed.current = true
	}

	return (
		<div className={['flex w-full min-w-0 gap-2', className].filter(Boolean).join(' ')}>
			<div
				className="w-[11rem] min-w-[11rem] shrink-0"
				onPointerDownCapture={markPickerUsed}
				onKeyDownCapture={markPickerUsed}
				onFocusCapture={markPickerUsed}
			>
				<Autocomplete
					key={`${country || 'empty'}-${pickerEpoch}`}
					isRequired={isRequired}
					isClearable={false}
					isDisabled={locked}
					allowsCustomValue={false}
					label="Phone Country"
					selectedKey={country || null}
					defaultInputValue={callingCodeFor(country)}
					defaultItems={PHONE_COUNTRY_ITEMS}
					isInvalid={countryInvalid}
					defaultFilter={matchesPhoneCountry}
					inputProps={{ autoComplete: 'off' }}
					className="w-full"
					popoverProps={{ className: '!w-80' }}
					onSelectionChange={(key: Key | null) => {
						if (!countryPickerUsed.current) return
						if (key == null || key === 'divider') return
						const next = String(key)
						if (!isCountryCode(next)) return
						if (next === country) {
							setPickerEpoch((epoch) => epoch + 1)
							return
						}
						emit(next, national)
					}}
				>
					{(item: PhoneCountryItem) =>
						isPhoneCountryOption(item) ? (
							<AutocompleteItem
								key={item.code}
								className="gap-2"
								textValue={phoneCountryDisplayName(item)}
								startContent={
									<Avatar
										alt={item.label}
										className="!size-6 text-xs"
										src={`https://flagcdn.com/${item.code.toLowerCase()}.svg`}
									/>
								}
							>
								{phoneCountryDisplayName(item)}
							</AutocompleteItem>
						) : (
							<AutocompleteItem key="divider" className="pointer-events-none p-0" textValue=" ">
								<Divider className="my-1" />
							</AutocompleteItem>
						)
					}
				</Autocomplete>
			</div>

			<Input
				className="min-w-0 flex-1"
				label={label}
				isRequired={isRequired}
				isReadOnly={isReadOnly}
				isDisabled={isDisabled}
				isClearable={isClearable && !locked}
				type="tel"
				inputMode="tel"
				autoComplete="tel-national"
				value={national}
				isInvalid={numberInvalid}
				errorMessage={errorMessage}
				onBlur={onBlur}
				onClear={
					isClearable && !locked
						? () => {
								emit(country, '')
								onClear?.()
							}
						: undefined
				}
				onValueChange={(nextValue: string) => {
					const trimmed = nextValue.trim()
					if (trimmed.startsWith('+')) {
						const parsed = parsePhone(trimmed)
						if (parsed) {
							emit(parsed.country, parsed.nationalNumber)
							return
						}
					}
					emit(country, sanitizeNationalNumber(nextValue))
				}}
			/>
		</div>
	)
}
